import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import { calculateStreamResolution } from 'features/kestrelViewer/utils/streamResolution'
import { RefObject, useEffect, useRef } from 'react'
import { KVResolutionQuality } from 'types'

const DEBOUNCE_TIME = 100 // milliseconds

export function useStreamResolution(
  pixelStreaming: PixelStreaming | undefined,
  videoParent: RefObject<HTMLDivElement | null>,
  resolutionQuality: KVResolutionQuality,
  videoReady: boolean
) {
  const lastResolutionRef = useRef<{ width: number; height: number } | null>(
    null
  )

  useEffect(() => {
    if (!pixelStreaming || !videoParent.current || !videoReady) return

    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const emitResolution = () => {
      if (!videoParent.current) return

      const { width, height } = calculateStreamResolution(
        videoParent.current.clientWidth,
        videoParent.current.clientHeight,
        resolutionQuality
      )

      const last = lastResolutionRef.current
      if (last && last.width === width && last.height === height) return

      lastResolutionRef.current = { width, height }
      pixelStreaming.emitCommand({
        'Resolution.Width': width,
        'Resolution.Height': height,
      })
    }

    // only send every 100 ms to avoid spamming as user resizes
    const debouncedEmit = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(emitResolution, DEBOUNCE_TIME)
    }

    emitResolution()

    const resizeObserver = new ResizeObserver(debouncedEmit)
    resizeObserver.observe(videoParent.current)

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      resizeObserver.disconnect()
    }
  }, [pixelStreaming, videoParent, resolutionQuality, videoReady])
}
